import React, { useState, useEffect } from 'react';
import { HashRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import Papa from 'papaparse';
import Header from './components/Header';
import EpisodeList from './components/EpisodeList';
import AudioPlayer from './components/AudioPlayer';
import HighlightedVideos from './components/HighlightedVideos';
import PlaylistSection from './components/PlaylistSection';
import NewsletterSignup from './components/NewsletterSignup';
import Footer from './components/Footer';
import RadioPlayerWidget from './components/RadioPlayerWidget';
import LatestEpisodeHero from './components/LatestEpisodeHero';
import LivePage from './components/LivePage';

function PodcastHome() {
  const [episodes, setEpisodes] = useState([]);
  const [currentEpisode, setCurrentEpisode] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEpisodes = async () => {
      try {
        const response = await fetch('https://docs.google.com/spreadsheets/d/1PN5mtJnsTJtwGCoUxia7F2Qvpy0k4FK0fn0DiLSDrsA/export?format=csv');
        const csvText = await response.text();
        Papa.parse(csvText, {
          header: false,
          complete: (results) => {
            const validRows = results.data.filter(row => row[0] && row[1] && row[1].startsWith('http'));
            const mappedEpisodes = validRows.reverse().map((row, index) => ({
              id: index,
              title: row[0],
              url: row[1],
              image: row[2] || '',
              description: row[3] || '',
              date: 'Nytt avsnitt'
            }));
            setEpisodes(mappedEpisodes);
            setLoading(false);
          },
          error: (error) => {
            console.error('Error parsing CSV:', error);
            setLoading(false);
          }
        });
      } catch (error) {
        console.error('Error fetching episodes:', error);
        setLoading(false);
      }
    };
    fetchEpisodes();
  }, []);

  const handlePlay = (episode) => {
    if (currentEpisode?.url === episode.url) {
      setIsPlaying(!isPlaying);
    } else {
      setCurrentEpisode(episode);
      setIsPlaying(true);
    }
  };

  const latestEpisode = episodes[0];
  const previousEpisodes = episodes.slice(1, 6);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {loading ? (
          <div className="flex justify-center py-24">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F28423]"></div>
          </div>
        ) : (
          <>
            <LatestEpisodeHero 
              episode={latestEpisode} 
              isPlaying={isPlaying && currentEpisode?.url === latestEpisode?.url} 
              onPlay={handlePlay} 
            />
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
              <div className="lg:col-span-8">
                <EpisodeList 
                  episodes={previousEpisodes} 
                  currentEpisode={currentEpisode} 
                  isPlaying={isPlaying} 
                  onPlay={handlePlay} 
                />
              </div>
              <div className="lg:col-span-4 space-y-8">
                <RadioPlayerWidget />
                <div className="bg-orange-50 rounded-2xl p-6 border border-orange-100">
                  <h3 className="font-bold text-gray-900 mb-2">Om Podden</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Effekten handlar om digitalisering och den påverkan den har på oss människor och samhället.
                  </p>
                  <a 
                    href="https://www.effekten.se" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-sm font-semibold text-[#F28423] hover:underline"
                  >
                    Läs mer på effekten.se &rarr;
                  </a>
                </div>
              </div>
            </div>
          </>
        )}
        <div className="mt-8 border-t border-gray-100 pt-4">
          <HighlightedVideos />
          <PlaylistSection />
          <NewsletterSignup />
        </div>
      </main>
      <Footer />
      {currentEpisode && (
        <AudioPlayer 
          currentEpisode={currentEpisode} 
          isPlaying={isPlaying} 
          setIsPlaying={setIsPlaying} 
        />
      )}
    </div>
  );
}

function App() {
  return (
    <HashRouter>
      <Routes>
        <Route path="/" element={<PodcastHome />} />
        <Route path="/live" element={<LivePage />} />
      </Routes>
    </HashRouter>
  );
}

export default App;